import dayjs from "dayjs";
import * as yup from "yup";
import type { IMetaActionParameter, IMetaColumn } from "../types/scad";
import type { IGenericForm } from "../types/generic";
import type { IFieldConfig } from "../types/scad";

// String custom methods
yup.addMethod(yup.string, "alpha", function alpha() {
  return this.transform((curr, orig) => (orig === "" ? null : curr)).matches(
    new RegExp(/^[a-zA-Z]+$/),
    {
      message: "${label} must only contain alphabetical characters",
      name: "alpha",
      excludeEmptyStrings: true,
    },
  );
});
yup.addMethod(yup.string, "alphanumeric", function alphanumeric() {
  return this.transform((curr, orig) => (orig === "" ? null : curr)).matches(
    new RegExp(/^[a-zA-Z0-9]+$/),
    {
      message: "${label} must only contain alphanumerical characters",
      name: "alphanumeric",
      excludeEmptyStrings: true,
    },
  );
});
yup.addMethod(yup.date, "minAge", function minAge(minAge: number) {
  return this.test(
    "minAge",
    `Must be at least ${minAge} years old`,
    function (value): boolean {
      if (!value) return true;

      const cutOffDate = dayjs().subtract(minAge, "year").toDate();
      return value <= cutOffDate;
    },
  );
});
yup.addMethod(
  yup.string,
  "phoneNumber",
  function phoneNumber(phoneNumber: number) {
    return this.test(
      "phoneNumber",
      `Must be a valid phone number`,
      function (value): boolean {
        if (!value) return true;

        const phoneRegex =
          /(?:([+]\d{1,4})[-.\s]?)?(?:[(](\d{1,3})[)][-.\s]?)?(\d{1,4})[-.\s]?(\d{1,4})[-.\s]?(\d{1,9})/gm;
        return phoneRegex.test(value);
      },
    );
  },
);

// Date custom methods
yup.addMethod(yup.date, "afterToday", function afterToday() {
  return this.min(new Date(), "${label} needs to be after today");
});
yup.addMethod(yup.date, "beforeToday", function beforeToday() {
  return this.max(new Date(), "${label} needs to be before today");
});
yup.addMethod(yup.date, "dateAfter", function (date: any) {
  // if date is a string and param starts with @, it is a reference to another field
  if (typeof date == "string" && date.startsWith("$")) {
    const convertedDate = convertDateExpressionToDate(date.slice(1));
    return this.min(
      convertedDate,
      "${label} needs to be after the specified date",
    );
  }
  // if date is a string and param starts with $, it is a date expression
  if (typeof date == "string" && date.startsWith("@")) {
    const fieldName = date.slice(1);
    return this.test({
      name: "dateAfter",
      message: "${label} needs to be after the specified date",
      test: function (value) {
        const { parent } = this;
        // Get the other date value from the parent object
        const compareTo = parent[fieldName];
        // If either date is not set, consider it valid (or adjust as needed)
        if (!value || !compareTo) return true;
        // Check that value is after compareTo
        return new Date(value) > new Date(compareTo);
      },
    });
  }

  return this.min(
    new Date(date),
    "${label} needs to be after the specified date",
  );
});

yup.addMethod(yup.date, "dateBefore", function (date) {
  // if param starts with @, it is a reference to another field
  if (typeof date == "string" && date.startsWith("$")) {
    const convertedDate = convertDateExpressionToDate(date.slice(1));
    return this.max(
      convertedDate,
      "${label} needs to be before the specified date",
    );
  }
  // if param starts with $, it is a date expression
  if (typeof date == "string" && date.startsWith("@")) {
    const fieldName = date.slice(1);
    return this.test({
      name: "dateBefore",
      message: "${label} needs to be before the specified date",
      test: function (value) {
        const { parent } = this;
        // Get the other date value from the parent object
        const compareTo = parent[fieldName];
        // If either date is not set, consider it valid (or adjust as needed)
        if (!value || !compareTo) return true;
        // Check that value is before compareTo
        return new Date(value) < new Date(compareTo);
      },
    });
  }

  return this.max(
    new Date(date),
    "${label} needs to be before the specified date",
  );
});

yup.addMethod(
  yup.string,
  "unique",
  function unique(tableName: string, schemaName: string, columnName: string) {
    return this.test({
      name: "unique",
      message: "${label} already exists",
      test: async function (value, context) {
        if (
          context.path in context.parent?._originalRecord &&
          context.parent?._originalRecord[context.path] == value
        )
          return true;

        try {
          const response = await $fetch(
            `/scad/meta/util/exists/${schemaName}/${tableName}/${columnName}/${value}`,
          );
          return !(response.Result ?? true);
        } catch (e) {
          return false;
        }
      },
    });
  },
);

yup.addMethod(
  yup.string,
  "exists",
  function exists(tableName: string, schemaName: string, columnName: string) {
    return this.test({
      name: "exists",
      message: "${label} is not valid",
      test: async function (value) {
        // Validation url `/scad/meta/util/exists/{Schema}/{Table}/{Column}/{Value}`
        try {
          const response = await $fetch(
            `/scad/meta/util/exists/${schemaName}/${tableName}/${columnName}/${value}`,
          );
          return response.Result ?? true;
        } catch (e) {
          return true;
        }
      },
    });
  },
);

export interface ValidationRule {
  field: string;
  schema: yup.Schema<any>;
}

const forbiddenRules = ["number", "[]"];

export function deriveValidationRules(
  rules: string | string[],
  initialSchema: yup.Schema<any>,
  label?: string,
  exclusions: string[] = [],
): yup.Schema<any> {
  const arrayRegexp = new RegExp(/\[(\w+)\]/g);
  let schema = initialSchema;
  if (label) {
    schema = schema.label(label);
  }

  if (typeof rules === "string") {
    // A comma-separated rule string used to be parsed into an empty list, so
    // every string-form rule - `required` included - was silently dropped and
    // the field went unvalidated. Split on top-level commas only, so rules that
    // carry bracketed parameters (`between[1,10]`) survive intact.
    const ruleList: string[] = [];
    let current = "";
    let bracketDepth = 0;

    for (const char of rules) {
      if (char === "[") {
        bracketDepth++;
        current += char;
      } else if (char === "]") {
        bracketDepth = Math.max(0, bracketDepth - 1);
        current += char;
      } else if (char === "," && bracketDepth === 0) {
        if (current.trim()) {
          ruleList.push(current.trim());
        }
        current = "";
      } else {
        current += char;
      }
    }

    if (current.trim()) {
      ruleList.push(current.trim());
    }

    const simpleRuleRegexp = /\[(\w+)\]/;
    const strippedRuleList = ruleList.map((rule) => {
      if (simpleRuleRegexp.test(rule)) {
        return rule.slice(1, -1);
      }
      return rule;
    });

    if (!strippedRuleList.includes("required")) {
      schema = schema.notRequired();
    }

    strippedRuleList.forEach((rule) => {
      if (!forbiddenRules.includes(rule) && !exclusions.includes(rule)) {
        schema = applyValidationRuleFromString(schema, rule);
      }
    });
  } else if (rules instanceof Array) {
    // Do array based validation
    const ruleList = rules.map((rule) => {
      if (arrayRegexp.test(rule)) {
        return rule.slice(1, -1);
      } else {
        return rule;
      }
    });

    if (!ruleList.includes("required")) {
      schema = schema.notRequired();
    }

    ruleList.forEach((rule) => {
      if (!forbiddenRules.includes(rule) && !exclusions.includes(rule)) {
        schema = applyValidationRuleFromString(schema, rule);
      }
    });
  }
  return schema;
}

// Rules that are real metadata, but belong to a control rather than to the schema. They are
// consumed elsewhere and are correctly absent from Yup, so skip them WITHOUT a warning - a
// warning here would be noise on every form that carries one.
const CONTROL_HANDLED_RULES = new Set([
  // read by components/form/field/index.vue -> `accepts`, enforced by field/fileUpload.vue
  "acceptFileTypes",
]);

function applyValidationRuleFromString(
  schema: yup.Schema<any>,
  rule: string,
): yup.Schema<any> {
  if (!rule) {
    return schema;
  }

  //console.log(`checking rule '${rule}' on schema:`, schema)

  let changeableSchema = schema;

  const arrayRegexp = new RegExp(/\[([^\[\]]+)\]/g);

  if (arrayRegexp.test(rule)) {
    rule = rule.slice(1, -1);
  }

  const ruleParams = rule.split(",");

  //console.log(`checking ruleParams:`, ruleParams);

  try {
    if (ruleParams[0] == "date") {
      return changeableSchema;
    }

    switch (ruleParams[0]) {
      case "between":
        if (schema.type == "date") {
          // SdW: commented out.  Date validation will be applied by disabling invalid date selection on the date selection control
          //  the problme with applying it here is that relative dates (like yesterday) which are correcty set initially,
          //  incorrectly fail validation when the same record is updated without changing the date.
          //
          // changeableSchema = (changeableSchema as yup.DateSchema)
          //   .min(ruleParams[1].charAt(0) === '@'
          //     ? yup.ref(ruleParams[1].slice(1))
          //     : ruleParams[1].charAt(0) === '$'
          //       ? convertDateExpressionToDate(ruleParams[1].slice(1))
          //       : ruleParams[1])
          //   .max(ruleParams[2].charAt(0) === '@'
          //     ? yup.ref(ruleParams[2].slice(1))
          //     : ruleParams[2].charAt(0) === '$'
          //     ? convertDateExpressionToDate(ruleParams[2].slice(1))
          //     : ruleParams[2])
        } else {
          changeableSchema = (changeableSchema as yup.NumberSchema)
            .min(
              ruleParams[1].charAt(0) === "@"
                ? yup.ref(ruleParams[1].slice(1))
                : parseInt(ruleParams[1]),
            )
            .max(
              ruleParams[2].charAt(0) === "@"
                ? yup.ref(ruleParams[2].slice(1))
                : parseInt(ruleParams[2]),
            );
        }
        break;
      case "requiredIf":
        changeableSchema = changeableSchema.test(
          "required-if",
          "${label} is required",
          async (value, testContext) => {
            const evaluator = new ExpressionEvaluator();
            const isRequired = evaluator.evaluateExpression(
              ruleParams[1],
              testContext.parent,
            );
            if (isRequired) {
              return value !== undefined && value !== null && value !== "";
            }

            return true;
          },
        );
        break;
      case "not":
        changeableSchema = (schema as { [key: string]: any })[ruleParams[0]](
          [...ruleParams.slice(1).map((val) => String(val).toLowerCase())],
          `Cannot be: ${ruleParams.slice(1).join(", ")}`,
        );
        break;
      case "matches":
        // Regex is the second parameter merged with all parameters until the second last
        const strippedRule = rule.replace("matches,", "");
        const indexOfLastComma = strippedRule.lastIndexOf(",");
        const regex = strippedRule.slice(
          0,
          indexOfLastComma >= 0 ? indexOfLastComma : strippedRule.length,
        );

        const regexTest = new RegExp(regex);

        // @ts-ignore
        if (indexOfLastComma >= 0) {
          changeableSchema = (changeableSchema as yup.StringSchema).matches(
            new RegExp(regex),
            ruleParams[ruleParams.length - 1],
          );
        } else {
          changeableSchema = (changeableSchema as yup.StringSchema).matches(
            new RegExp(regex),
            `Invalid format.`,
          );
        }
        break;

      case "lessThan":
        // number.lessThan(max: number | Ref, message?: string | function): Schema

        changeableSchema = (changeableSchema as yup.NumberSchema).lessThan(
          ruleParams[1].charAt(0) === "@"
            ? yup.ref(ruleParams[1].slice(1))
            : parseInt(ruleParams[1]),
          ruleParams[2].charAt(0)
            ? ruleParams[2]
            : `Must be less than ${ruleParams[1]}`,
        );

        break;

      case "moreThan":
        // number.moreThan(min: number | Ref, message?: string | function): Schema

        changeableSchema = (changeableSchema as yup.NumberSchema).moreThan(
          ruleParams[1].charAt(0) === "@"
            ? yup.ref(ruleParams[1].slice(1))
            : parseInt(ruleParams[1]),
          ruleParams[2].charAt(0)
            ? ruleParams[2]
            : `Must be more than ${ruleParams[1]}`,
        );

        break;

      default:
        // Everything that reaches `default` is invoked as schema[ruleParams[0]](...), which
        // assumes the rule name is a method on the Yup schema. Not every rule is one:
        // `acceptFileTypes` is metadata for the file-upload control (field/index.vue turns it
        // into the `accepts` prop, field/fileUpload.vue enforces it on pick and on drop), so
        // Yup has no such method and the call threw `schema[ruleParams[0]] is not a function`.
        // The catch below swallowed that as a console error on every Add form across the 20
        // views carrying the rule. Skip an unsupported name deliberately instead.
        //
        // The name is also a property lookup, so one that resolves to something inherited from
        // Object.prototype - `constructor`, `toString`, `valueOf` - would pass a bare typeof
        // check and then be invoked. Rule names come from meta.meta_column, which is
        // admin-editable, so reject those by name rather than trusting nobody writes one.
        if (
          Object.prototype.hasOwnProperty.call(
            Object.prototype,
            ruleParams[0],
          ) ||
          typeof (schema as { [key: string]: any })[ruleParams[0]] !==
            "function"
        ) {
          if (!CONTROL_HANDLED_RULES.has(ruleParams[0])) {
            console.warn(
              `Validation rule '${ruleParams[0]}' is not a Yup schema method and is not handled by a control - skipping it.`,
            );
          }
          break;
        }

        if (schema.type == "date") {
          // SdW: commented out.  Date validation will be applied by disabling invalid date selection on the date selection control
          //  the problme with applying it here is that relative dates (like yesterday) which are correcty set initially,
          //  incorrectly fail validation when the same record is updated without changing the date.

          // if (ruleParams[0] == 'min') {
          //     changeableSchema = (changeableSchema as yup.DateSchema).min(
          //       ruleParams[1].charAt(0) === '@'
          //       ? yup.ref(ruleParams[1].slice(1))
          //       : ruleParams[1].charAt(0) === '$'
          //       ? convertDateExpressionToDate(ruleParams[1].slice(1))
          //       : ruleParams[1]
          //       , ruleParams[1].charAt(0) === '$'
          //         ? `Cannot be before ${ruleParams[1].slice(1)}`
          //         : undefined
          //     )
          // } else if (ruleParams[0] == 'max') {
          //     changeableSchema = (changeableSchema as yup.DateSchema).max(
          //       ruleParams[1].charAt(0) === '@'
          //       ? yup.ref(ruleParams[1].slice(1))
          //       : ruleParams[1].charAt(0) === '$'
          //       ? convertDateExpressionToDate(ruleParams[1].slice(1))
          //       : ruleParams[1]
          //       , ruleParams[1].charAt(0) === '$'
          //         ? `Cannot be after ${ruleParams[1].slice(1)}`
          //         : undefined
          //     )
          // }
          // else {
          if (ruleParams[0] == "date") {
            //skip it...its the rule that says this should be a data schema, which would already have been applied in the initial schema
          } else {
            changeableSchema = (schema as { [key: string]: any })[
              ruleParams[0]
            ](...ruleParams.slice(1));
          }
          //}
        } else {
          changeableSchema = (schema as { [key: string]: any })[ruleParams[0]](
            ...ruleParams.slice(1),
          );
        }
        break;
    }
  } catch (e) {
    // Error validating rule
  }

  return changeableSchema;
}

export class ValidationEngine {
  private validations: ValidationRule[];

  constructor(validations: ValidationRule[]) {
    this.validations = validations;
  }

  validate(dataObject: any): Promise<any> {
    let validationSchema = yup.object();

    // Add validation rules to the schema
    this.validations.forEach((validation) => {
      validationSchema = validationSchema.shape({
        [validation.field]: validation.schema,
      });
    });

    // Perform validation

    return validationSchema.validate(dataObject, { abortEarly: false });
  }

  async validateToString(
    dataObject: any,
    returnValidated: boolean = false,
  ): Promise<any> {
    try {
      const validatedValues = await this.validate(dataObject);
      return returnValidated ? validatedValues : {};
    } catch (validationError: any) {
      const errors =
        validationError?.inner?.reduce(
          (acc: { [key: string]: string }, err: any) => {
            acc[err.path] = err.message;
            return acc;
          },
          {},
        ) ?? {};

      return errors;
    }
  }
}

function getValidationObjectFromType(
  type: string,
  field?: string,
  isRequired?: boolean,
) {
  switch (type) {
    case "number":
      return yup.number().nullable();
    case "string":
      return yup
        .string()
        .nullable()
        .transform((o, c) => (o === "" ? null : c));
    case "date":
    case "datetime":
      return yup.date().nullable();
    case "bool":
      if (isRequired === true) {
        return yup
          .bool()
          .required(field ? `${field} is required` : "This field is required");
      } else {
        return yup.bool().nullable();
      }
    case "array":
      // if field is required, array should contain at least one value
      if (isRequired == true) {
        return yup
          .array()
          .min(
            1,
            field
              ? `${field} must have at least 1 item selected`
              : "At least 1 item must be selected",
          );
      }

      return yup.array();
    case "none":
      return undefined;
    default:
      return yup.string();
  }
}

export function getValidationRulesMapFromMetaColumns(
  columns: IMetaColumn[],
  key: "Validation" | "OptionalValidation" = "Validation",
  exclusions: string[] = [],
): {
  [key: string]: ValidationRule;
} {
  const validationRules: { [key: string]: ValidationRule } = {};
  columns.forEach((col) => {
    if (col[key]) {
      let validationObject = getValidationObjectFromType(
        // @ts-ignore
        deriveTypeFromControlType(col.ControlType),
        col.ColumnLabel,
        (col.Validation as String[])?.flat()?.join("")?.includes("required") ??
          false,
      );

      if (validationObject === undefined) {
        return; // skip adding validation rule if type is 'none'
      }

      validationRules[col.ColumnNamePc] = {
        schema: deriveValidationRules(
          // @ts-ignore
          col[key],
          validationObject,
          col.ColumnLabel,
          exclusions,
        ),
        field: col.ColumnNamePc,
      };
    }
  });

  return validationRules;
}

export function getValidationRulesMapFromFormField(
  fields: IFieldConfig[],
  key: "validation" | "optionalValidation" = "validation",
): {
  [key: string]: ValidationRule;
} {
  const validationRules: { [key: string]: ValidationRule } = {};

  fields.forEach((field) => {
    if (field[key]) {
      let validationObject = getValidationObjectFromType(
        // @ts-ignore
        deriveTypeFromInputType(field.InputType),
        field.label,
        (field.validation as String[])
          ?.flat()
          ?.join("")
          ?.includes("required") ?? false,
      );

      if (validationObject === undefined) {
        return; // skip adding validation rule if type is 'none'
      }

      validationRules[field.name] = {
        schema: deriveValidationRules(
          // @ts-ignore
          field[key],
          validationObject,
          field.label,
        ),
        field: field.name,
      };
    }
  });

  return validationRules;
}

export function getValidationRulesMapFromMetaActionParameters(
  parameters: IMetaActionParameter[],
  key: "Validation" | "OptionalValidation" = "Validation",
): {
  [key: string]: ValidationRule;
} {
  const validationRules: { [key: string]: ValidationRule } = {};

  parameters.forEach((param) => {
    if ((param as Indexable)[key]) {
      let validationObject = getValidationObjectFromType(
        deriveTypeFromControlType(param.ControlType ?? ""),
        param.FieldLabel,
        (param.Validation as String[])
          ?.flat()
          ?.join("")
          ?.includes("required") ?? false,
      );

      if (validationObject === undefined) {
        return; // skip adding validation rule if type is 'none'
      }

      validationRules[param.Key] = {
        schema: deriveValidationRules(
          // @ts-ignore
          param[key],
          validationObject,
          param.FieldLabel,
        ),
        field: param.Key,
      };
    }
  });

  return validationRules;
}

export function getValidationRulesFromMetaColumns(
  columns: IMetaColumn[],
  key: "Validation" | "OptionalValidation" = "Validation",
): ValidationRule[] {
  const validationRules: ValidationRule[] = [];
  columns.forEach((col) => {
    if (col[key]) {
      let validationObject = getValidationObjectFromType(
        // @ts-ignore
        deriveTypeFromControlType(col.ControlType),
        col.ColumnLabel,
        (col.Validation as String[])?.flat()?.join("")?.includes("required") ??
          false,
      );

      if (validationObject === undefined) {
        return; // skip adding validation rule if type is 'none'
      }

      validationRules.push({
        schema: deriveValidationRules(
          // @ts-ignore
          col[key],
          validationObject,
          col.ColumnLabel,
        ),
        field: col.ColumnNamePc,
      });
    }
  });

  return validationRules;
}

export async function validateGenericForm(form: IGenericForm) {
  var errCount = 0;

  for (var field in form) {
    if (form[field].hide) {
      continue;
    }
    try {
      if (form[field].label) {
        await form[field].validation
          ?.label(form[field].label as string)
          .validate(form[field].value);
      } else {
        await form[field].validation?.validate(form[field].value);
      }
      form[field].errors = undefined;
    } catch (e: any) {
      form[field].errors = [e.message];
      errCount++;
    }
  }
  // return true/false valid based on error count
  return errCount == 0;
}
